import React, { useState, useEffect, useMemo } from 'react';
import { authAPI } from '../../../api/auth';
import { usePairsConfig } from '../../../hooks/usePairsConfig';
import {
  getPairSettingsKey,
  getPairOverviewDisplayKey,
  isPairShownInOverview,
} from '../../../utils/pairSettings';
import './settings.css';

const GearIcon = () => (
  <svg
    className="toggle-settings-icon"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1Z" />
  </svg>
);

const ToggleSwitch = ({
  id,
  label,
  checked,
  onChange,
  onSettingsClick,
  settingsOpen,
}) => (
  <div className="toggle-row">
    <span className="toggle-label">{label}</span>
    <div className="toggle-controls">
      {onSettingsClick && (
        <button
          type="button"
          className={`toggle-settings-btn${settingsOpen ? ' toggle-settings-btn--active' : ''}`}
          onClick={onSettingsClick}
          aria-label={`${label} pair settings`}
          aria-expanded={settingsOpen}
        >
          <GearIcon />
        </button>
      )}
      <span className={`toggle-state ${checked ? 'toggle-state--on' : 'toggle-state--off'}`}>
        {checked ? 'On' : 'Off'}
      </span>
      <label htmlFor={id} className="toggle-switch" aria-label={`${label}: ${checked ? 'On' : 'Off'}`}>
        <input
          id={id}
          type="checkbox"
          className="toggle-input"
          checked={checked}
          onChange={onChange}
        />
        <span className="toggle-track">
          <span className="toggle-thumb" />
        </span>
      </label>
    </div>
  </div>
);

const PairConfigRow = ({
  pair,
  pairFlags,
  overviewDisplayFlags,
  pairSettings,
  openPairSettings,
  setPairFlag,
  setOverviewDisplayFlag,
  togglePairSettingsOpen,
  updatePairSetting,
}) => {
  const settingsKey = getPairSettingsKey(pair);
  const currentSettings = pairSettings[settingsKey] || {};
  const isOpen = !!openPairSettings[settingsKey];

  return (
    <div className="pair-settings-wrapper">
      <ToggleSwitch
        id={`toggle-${pair.config}`}
        label={pair.toggleLabel}
        checked={!!pairFlags[pair.config]}
        onChange={(e) => setPairFlag(pair.config, e.target.checked)}
        onSettingsClick={() => togglePairSettingsOpen(settingsKey)}
        settingsOpen={isOpen}
      />
      {isOpen && (
        <div className="pair-settings-panel">
          <div className="form-group">
            <label className="form-label" htmlFor={`overview-${settingsKey}`}>
              Display in Overview
            </label>
            <div className="pair-setting-toggle">
              <span
                className={`toggle-state ${overviewDisplayFlags[settingsKey] ? 'toggle-state--on' : 'toggle-state--off'}`}
              >
                {overviewDisplayFlags[settingsKey] ? 'On' : 'Off'}
              </span>
              <label
                htmlFor={`overview-${settingsKey}`}
                className="toggle-switch"
                aria-label={`Display in Overview: ${overviewDisplayFlags[settingsKey] ? 'On' : 'Off'}`}
              >
                <input
                  id={`overview-${settingsKey}`}
                  type="checkbox"
                  className="toggle-input"
                  checked={!!overviewDisplayFlags[settingsKey]}
                  onChange={(e) => setOverviewDisplayFlag(settingsKey, e.target.checked)}
                />
                <span className="toggle-track">
                  <span className="toggle-thumb" />
                </span>
              </label>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Side Minimal Amount</label>
            <input
              type="number"
              className="form-input"
              value={currentSettings.side_min_amt || ''}
              onChange={(e) => updatePairSetting(settingsKey, 'side_min_amt', e.target.value)}
              placeholder="Use global if empty"
            />
          </div>
          <div className="form-group">
            <label className="form-label">Maximum Trades Amount</label>
            <input
              type="number"
              className="form-input"
              value={currentSettings.max_amt || ''}
              onChange={(e) => updatePairSetting(settingsKey, 'max_amt', e.target.value)}
              placeholder="Use global if empty"
            />
          </div>
          <div className="form-group">
            <label className="form-label">Minimal Price</label>
            <input
              type="number"
              step="0.000001"
              className="form-input"
              value={currentSettings.min_price || ''}
              onChange={(e) => updatePairSetting(settingsKey, 'min_price', e.target.value)}
              placeholder="Use global if empty"
            />
          </div>
          <div className="form-group">
            <label className="form-label">Maximum Price</label>
            <input
              type="number"
              step="0.000001"
              className="form-input"
              value={currentSettings.max_price || ''}
              onChange={(e) => updatePairSetting(settingsKey, 'max_price', e.target.value)}
              placeholder="Use global if empty"
            />
          </div>
        </div>
      )}
    </div>
  );
};

const Settings = ({ themeMode = 'light' }) => {
  const { overviewPairs, loading: pairsLoading } = usePairsConfig();
  const mexcPairs = useMemo(
    () => overviewPairs.filter((p) => p.platform === 'mexc'),
    [overviewPairs]
  );
  const binancePairs = useMemo(
    () => overviewPairs.filter((p) => p.platform === 'binance'),
    [overviewPairs]
  );
  const okxPairs = useMemo(
    () => overviewPairs.filter((p) => p.platform === 'okx'),
    [overviewPairs]
  );

  const [mexcApiKey, setMexcApiKey] = useState('');
  const [mexcApiSecret, setMexcApiSecret] = useState('');
  const [binanceApiKey, setBinanceApiKey] = useState('');
  const [binanceApiSecret, setBinanceApiSecret] = useState('');
  const [okxApiKey, setOkxApiKey] = useState('');
  const [okxApiSecret, setOkxApiSecret] = useState('');
  const [okxApiPassphrase, setOkxApiPassphrase] = useState('');
  const [pairFlags, setPairFlags] = useState({});
  const [overviewDisplayFlags, setOverviewDisplayFlags] = useState({});
  const [pairSettings, setPairSettings] = useState({});
  const [openPairSettings, setOpenPairSettings] = useState({});
  const [sideMinAmt, setSideMinAmt] = useState('');
  const [maxAmt, setMaxAmt] = useState('');
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');

  const setPairFlag = (configKey, value) => {
    setPairFlags((prev) => ({ ...prev, [configKey]: value }));
  };

  const setOverviewDisplayFlag = (settingsKey, value) => {
    setOverviewDisplayFlags((prev) => ({ ...prev, [settingsKey]: value }));
  };

  const togglePairSettingsOpen = (key) => {
    setOpenPairSettings((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const updatePairSetting = (key, field, value) => {
    setPairSettings((prev) => ({
      ...prev,
      [key]: {
        ...(prev[key] || {}),
        [field]: value,
      },
    }));
  };

  useEffect(() => {
    if (!pairsLoading && overviewPairs.length > 0) {
      loadApiKeys();
    }
  }, [pairsLoading, overviewPairs]);

  const loadApiKeys = async () => {
    try {
      const response = await authAPI.getApiKeys();

      if (response.success && response.data) {
        const data = response.data;
        if (data.mexc_api_key) {
          setMexcApiKey(data.mexc_api_key || '');
        }
        if (data.mexc_api_secret) {
          setMexcApiSecret(data.mexc_api_secret || '');
        }
        if (data.binance_api_key) {
          setBinanceApiKey(data.binance_api_key || '');
        }
        if (data.binance_api_secret) {
          setBinanceApiSecret(data.binance_api_secret || '');
        }
        if (data.okx_api_key) {
          setOkxApiKey(data.okx_api_key || '');
        }
        if (data.okx_api_secret) {
          setOkxApiSecret(data.okx_api_secret || '');
        }
        if (data.okx_api_passphrase) {
          setOkxApiPassphrase(data.okx_api_passphrase || '');
        }
        const truthy = (v) =>
          v === true || v === 1 || v === '1' || v === 'true';
        const hasOwn = (v) => v !== undefined && v !== null && v !== '';
        const legacyOn = truthy(data.binance_active);

        const flags = {};
        const overviewFlags = {};
        const perPairSettings = {};
        for (const pair of overviewPairs) {
          const settingsKey = getPairSettingsKey(pair);
          if (pair.config === 'binance_active_eur') {
            flags[pair.config] = hasOwn(data[pair.config]) ? truthy(data[pair.config]) : legacyOn;
          } else {
            flags[pair.config] = hasOwn(data[pair.config]) ? truthy(data[pair.config]) : false;
          }
          overviewFlags[settingsKey] = isPairShownInOverview(data, pair);
          perPairSettings[settingsKey] = {
            side_min_amt: data[`${settingsKey}_side_min_amt`] || '',
            max_amt: data[`${settingsKey}_max_amt`] || '',
            min_price: data[`${settingsKey}_min_price`] || '',
            max_price: data[`${settingsKey}_max_price`] || '',
          };
        }
        setPairFlags(flags);
        setOverviewDisplayFlags(overviewFlags);
        setPairSettings(perPairSettings);

        if (data.side_min_amt) {
          setSideMinAmt(data.side_min_amt || '');
        }
        if (data.max_amt) {
          setMaxAmt(data.max_amt || '');
        }
        if (data.min_price) {
          setMinPrice(data.min_price || '');
        }
        if (data.max_price) {
          setMaxPrice(data.max_price || '');
        }
      }
    } catch (error) {
      console.error('Error loading API keys:', error);
    }
  };

  const saveApiKeys = async () => {
    setLoading(true);
    setMessage('');

    try {
      const payload = {
        mexc_api_key: mexcApiKey,
        mexc_api_secret: mexcApiSecret,
        binance_api_key: binanceApiKey,
        binance_api_secret: binanceApiSecret,
        okx_api_key: okxApiKey,
        okx_api_secret: okxApiSecret,
        okx_api_passphrase: okxApiPassphrase,
        side_min_amt: sideMinAmt,
        max_amt: maxAmt,
        min_price: minPrice,
        max_price: maxPrice,
      };
      for (const pair of overviewPairs) {
        payload[pair.config] = !!pairFlags[pair.config];
        const settingsKey = getPairSettingsKey(pair);
        payload[getPairOverviewDisplayKey(pair)] = !!overviewDisplayFlags[settingsKey];
        const pairCfg = pairSettings[settingsKey] || {};
        payload[`${settingsKey}_side_min_amt`] = pairCfg.side_min_amt || '';
        payload[`${settingsKey}_max_amt`] = pairCfg.max_amt || '';
        payload[`${settingsKey}_min_price`] = pairCfg.min_price || '';
        payload[`${settingsKey}_max_price`] = pairCfg.max_price || '';
      }

      const response = await authAPI.saveApiKeys(payload);

      let message = 'API keys saved successfully!';

      if (response.validation) {
        const validationMessages = [];

        if (response.validation.mexc) {
          if (response.validation.mexc.valid) {
            validationMessages.push('✅ MEXC API credentials are valid');
          } else {
            validationMessages.push(`❌ MEXC API credentials are invalid: ${response.validation.mexc.message}`);
          }
        }

        if (response.validation.binance) {
          if (response.validation.binance.valid) {
            validationMessages.push('✅ Binance API credentials are valid');
          } else {
            validationMessages.push(`❌ Binance API credentials are invalid: ${response.validation.binance.message}`);
          }
        }

        if (response.validation.okx) {
          if (response.validation.okx.valid) {
            validationMessages.push('✅ OKX API credentials are valid');
          } else {
            validationMessages.push(`❌ OKX API credentials are invalid: ${response.validation.okx.message}`);
          }
        }

        if (validationMessages.length > 0) {
          message += '\n\n' + validationMessages.join('\n');
        }
      }

      setMessage(message);
      setMessageType('success');
    } catch (error) {
      let errorMessage = 'Error saving API keys. Please try again.';

      if (error.response?.data?.validation) {
        const validationMessages = [];

        if (error.response.data.validation.mexc && !error.response.data.validation.mexc.valid) {
          validationMessages.push(`❌ MEXC API credentials are invalid: ${error.response.data.validation.mexc.message}`);
        }

        if (error.response.data.validation.binance && !error.response.data.validation.binance.valid) {
          validationMessages.push(`❌ Binance API credentials are invalid: ${error.response.data.validation.binance.message}`);
        }

        if (error.response.data.validation.okx && !error.response.data.validation.okx.valid) {
          validationMessages.push(`❌ OKX API credentials are invalid: ${error.response.data.validation.okx.message}`);
        }

        if (validationMessages.length > 0) {
          errorMessage = validationMessages.join('\n');
        }
      }

      setMessage(errorMessage);
      setMessageType('error');
      console.error('Error saving API keys:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className={`settings-container${themeMode === 'dark' ? ' settings-container--dark' : ''}`}
    >
      <div className="settings-card">
        <h3 className="card-title">Exchange API Keys</h3>

        <div className="exchange-section mexc-section">
          <h4 className="exchange-title">
            <div className="exchange-icon mexc">M</div>
            Mexc Exchange
          </h4>
          <div className="form-group toggle-list">
            {pairsLoading ? (
              <span className="toggle-loading">Loading pairs…</span>
            ) : (
              mexcPairs.map((pair) => (
                <PairConfigRow
                  key={pair.config}
                  pair={pair}
                  pairFlags={pairFlags}
                  overviewDisplayFlags={overviewDisplayFlags}
                  pairSettings={pairSettings}
                  openPairSettings={openPairSettings}
                  setPairFlag={setPairFlag}
                  setOverviewDisplayFlag={setOverviewDisplayFlag}
                  togglePairSettingsOpen={togglePairSettingsOpen}
                  updatePairSetting={updatePairSetting}
                />
              ))
            )}
          </div>
          <div className="form-group">
            <label htmlFor="mexc-api-key" className="form-label">API Key</label>
            <input
              id="mexc-api-key"
              type="text"
              className="form-input"
              value={mexcApiKey}
              onChange={(e) => setMexcApiKey(e.target.value)}
              placeholder="Enter your Mexc API key"
            />
          </div>
          <div className="form-group">
            <label htmlFor="mexc-api-secret" className="form-label">API Secret</label>
            <input
              id="mexc-api-secret"
              type="password"
              className="form-input"
              value={mexcApiSecret}
              onChange={(e) => setMexcApiSecret(e.target.value)}
              placeholder="Enter your Mexc API secret"
            />
          </div>
        </div>

        <div className="exchange-section binance-section">
          <h4 className="exchange-title">
            <div className="exchange-icon binance">B</div>
            Binance Exchange
          </h4>
          <div className="form-group toggle-list">
            {pairsLoading ? (
              <span className="toggle-loading">Loading pairs…</span>
            ) : (
              binancePairs.map((pair) => (
                <PairConfigRow
                  key={pair.config}
                  pair={pair}
                  pairFlags={pairFlags}
                  overviewDisplayFlags={overviewDisplayFlags}
                  pairSettings={pairSettings}
                  openPairSettings={openPairSettings}
                  setPairFlag={setPairFlag}
                  setOverviewDisplayFlag={setOverviewDisplayFlag}
                  togglePairSettingsOpen={togglePairSettingsOpen}
                  updatePairSetting={updatePairSetting}
                />
              ))
            )}
          </div>
          <div className="form-group">
            <label htmlFor="binance-api-key" className="form-label">API Key</label>
            <input
              id="binance-api-key"
              type="text"
              className="form-input"
              value={binanceApiKey}
              onChange={(e) => setBinanceApiKey(e.target.value)}
              placeholder="Enter your Binance API key"
            />
          </div>
          <div className="form-group">
            <label htmlFor="binance-api-secret" className="form-label">API Secret</label>
            <input
              id="binance-api-secret"
              type="password"
              className="form-input"
              value={binanceApiSecret}
              onChange={(e) => setBinanceApiSecret(e.target.value)}
              placeholder="Enter your Binance API secret"
            />
          </div>
        </div>

        <div className="exchange-section okx-section">
          <h4 className="exchange-title">
            <div className="exchange-icon okx">O</div>
            OKX Exchange
          </h4>
          <div className="form-group toggle-list">
            {pairsLoading ? (
              <span className="toggle-loading">Loading pairs…</span>
            ) : (
              okxPairs.map((pair) => (
                <PairConfigRow
                  key={pair.config}
                  pair={pair}
                  pairFlags={pairFlags}
                  overviewDisplayFlags={overviewDisplayFlags}
                  pairSettings={pairSettings}
                  openPairSettings={openPairSettings}
                  setPairFlag={setPairFlag}
                  setOverviewDisplayFlag={setOverviewDisplayFlag}
                  togglePairSettingsOpen={togglePairSettingsOpen}
                  updatePairSetting={updatePairSetting}
                />
              ))
            )}
          </div>
          <div className="form-group">
            <label htmlFor="okx-api-key" className="form-label">API Key</label>
            <input
              id="okx-api-key"
              type="text"
              className="form-input"
              value={okxApiKey}
              onChange={(e) => setOkxApiKey(e.target.value)}
              placeholder="Enter your OKX API key"
            />
          </div>
          <div className="form-group">
            <label htmlFor="okx-api-secret" className="form-label">API Secret</label>
            <input
              id="okx-api-secret"
              type="password"
              className="form-input"
              value={okxApiSecret}
              onChange={(e) => setOkxApiSecret(e.target.value)}
              placeholder="Enter your OKX API secret"
            />
          </div>
          <div className="form-group">
            <label htmlFor="okx-api-passphrase" className="form-label">API Passphrase</label>
            <input
              id="okx-api-passphrase"
              type="password"
              className="form-input"
              value={okxApiPassphrase}
              onChange={(e) => setOkxApiPassphrase(e.target.value)}
              placeholder="Enter your OKX API passphrase"
            />
          </div>
        </div>

        <div className="exchange-section trading-section">
          <h4 className="exchange-title">
            <div className="exchange-icon trading">⚙️</div>
            Trading Settings
          </h4>
          <div className="form-group">
            <label htmlFor="side-min-amt" className="form-label">Side Minimal Amount</label>
            <input
              id="side-min-amt"
              type="number"
              className="form-input"
              value={sideMinAmt}
              onChange={(e) => setSideMinAmt(e.target.value)}
              placeholder="Enter side minimal amount (integer)"
            />
          </div>
          <div className="form-group">
            <label htmlFor="max-amt" className="form-label">Maximum Trades Amount</label>
            <input
              id="max-amt"
              type="number"
              className="form-input"
              value={maxAmt}
              onChange={(e) => setMaxAmt(e.target.value)}
              placeholder="Enter maximum trades amount (integer)"
            />
          </div>
          <div className="form-group">
            <label htmlFor="min-price" className="form-label">Minimal Price</label>
            <input
              id="min-price"
              type="number"
              step="0.000001"
              className="form-input"
              value={minPrice}
              onChange={(e) => setMinPrice(e.target.value)}
              placeholder="Enter minimal price (decimal)"
            />
          </div>
          <div className="form-group">
            <label htmlFor="max-price" className="form-label">Maximum Price</label>
            <input
              id="max-price"
              type="number"
              step="0.000001"
              className="form-input"
              value={maxPrice}
              onChange={(e) => setMaxPrice(e.target.value)}
              placeholder="Enter maximum price (decimal)"
            />
          </div>
        </div>

        <button className="save-button" onClick={saveApiKeys} disabled={loading || pairsLoading}>
          {loading ? 'Saving...' : 'Save Settings'}
        </button>

        {message && (
          messageType === 'success' ? (
            <div className="success-message">{message}</div>
          ) : (
            <div className="error-message">{message}</div>
          )
        )}
      </div>
    </div>
  );
};

export default Settings;

